import Failure from '../../../core/Failure';
import { UserRole } from '@leedpulse-monorepo/enums';
import { Authentication } from '@leedpulse-monorepo/classes';
import { AccountType, EProductType, UserType } from '@leedpulse-monorepo/enums';
import { ICreditMap } from '@leedpulse-monorepo/interfaces';
import HttpClient from '../../../core/HttpClient';
import SubscriptionTimezone from '../../models/subscription/SubscriptionTimezone';
import AIGender from '../../enums/ai/AIGender';
import SubscriptionTimezoneCity from '../../enums/subscription/subscription_timezone/SubscriptionTimezoneCity';
import SubscriptionTimezoneContinent from '../../enums/subscription/subscription_timezone/SubscriptionTimezoneContinent';
import SubscriptionTimezoneCountry from '../../enums/subscription/subscription_timezone/SubscriptionTimezoneCountry';
import AITon from '../../enums/ai/AITon';
import SubscriptionSettings from '../../models/subscription/SubscriptionSettings';
import SubscriptionDataSource from '../../enums/subscription/subscription_datasource/SubscriptionDataSource';

async function createUser(
  payload: CreateUserPayload,
  auth: Authentication
): Promise<void> {
  if (
    ![UserRole.owner, UserRole.admin, UserRole.maintainer].includes(auth.role)
  ) {
    throw new CreateUserFailure('Unauthorized', 'unauthorized');
  }
  try {
    const oPayload = payload.toAPIPayload();
    await HttpClient.post('/users', oPayload, auth.token, false, {
      'x-creator-email': auth.email,
    });

    return;
  } catch (e) {
    console.log('error: ', e);
    if (e instanceof TypeError && e.message === 'Failed to fetch') {
      throw new CreateUserFailure('Le serveur ne répond pas');
    } else if (e instanceof Error) {
      throw new CreateUserFailure(e.message);
    }
  }
}

export class UserInfosPayload {
  private _avatar: string;
  private _email: string;
  private _emailAuthentication: string;
  private _password: string;
  private _role: UserRole;
  private _firstname: string;
  private _lastname: string;
  private _phone: string;
  private _type: UserType;
  private _notes: string;

  constructor(
    avatar: string,
    email: string,
    emailAuthentication: string,
    password: string,
    role: UserRole,
    firstname: string,
    lastname: string,
    phone: string,
    type: UserType,
    notes: string
  ) {
    this._avatar = avatar;
    this._email = email;
    this._emailAuthentication = emailAuthentication;
    this._password = password;
    this._role = role;
    this._firstname = firstname;
    this._lastname = lastname;
    this._phone = phone;
    this._type = type;
    this._notes = notes;
  }

  // A Factory method with default values
  static newDefault(): UserInfosPayload {
    return new UserInfosPayload(
      '',
      '',
      '',
      '',
      UserRole.customer,
      '',
      '',
      '',
      UserType.USER,
      ''
    );
  }

  get avatar(): string {
    return this._avatar;
  }

  get email(): string {
    return this._email;
  }

  get emailAuthentication(): string {
    return this._emailAuthentication;
  }

  get password(): string {
    return this._password;
  }

  get role(): UserRole {
    return this._role;
  }

  get firstname(): string {
    return this._firstname;
  }

  get lastname(): string {
    return this._lastname;
  }

  get phone(): string {
    return this._phone;
  }

  get type(): UserType {
    return this._type;
  }

  get notes(): string {
    return this._notes;
  }

  copyWith(data: Partial<UserInfosPayload>): UserInfosPayload {
    return new UserInfosPayload(
      data.avatar ?? this._avatar,
      data.email ?? this._email,
      data.emailAuthentication ?? this._emailAuthentication,
      data.password ?? this._password,
      data.role ?? this._role,
      data.firstname ?? this._firstname,
      data.lastname ?? this._lastname,
      data.phone ?? this._phone,
      data.type ?? this._type,
      data.notes ?? this._notes
    );
  }

  toAPIPayload(): Record<string, any> {
    return {
      avatar: this._avatar,
      email: this._email,
      email_authentication: this._emailAuthentication,
      password: this._password,
      role: this._role,
      firstname: this._firstname,
      lastname: this._lastname,
      phone: this._phone,
      type: this._type,
      notes: this._notes,
    };
  }
}

export class SubscriptionPayload {
  private _idPlan: string;
  private _name: string;
  private _sender: string;
  private _timezone: SubscriptionTimezone;
  private _planStart: Date;
  private _planEnd: Date;
  private _settings: SubscriptionSettings;
  private _credits: ICreditMap;
  private _employeeInChargeUid: string;
  private _datasource: string;
  private _notes: string;

  constructor(
    idPlan: string,
    name: string,
    sender: string,
    timezone: SubscriptionTimezone,
    planStart: Date,
    planEnd: Date,
    credits: ICreditMap,
    settings: SubscriptionSettings = {},
    employeeInChargeUid: string,
    datasource: string,
    notes: string
  ) {
    this._idPlan = idPlan;
    this._name = name;
    this._sender = sender;
    this._timezone = timezone;
    this._planStart = planStart;
    this._planEnd = planEnd;
    this._settings = settings;
    this._credits = credits;
    this._employeeInChargeUid = employeeInChargeUid;
    this._datasource = datasource;
    this._notes = notes;
  }

  static newDefault(userId: string | undefined): SubscriptionPayload {
    const now = new Date(Date.now());
    const nowPlusOneYear = new Date(Date.now());
    nowPlusOneYear.setFullYear(nowPlusOneYear.getFullYear() + 1);
    return new SubscriptionPayload(
      's',
      '',
      '',
      new SubscriptionTimezone(
        SubscriptionTimezoneCity.paris,
        SubscriptionTimezoneContinent.europe,
        SubscriptionTimezoneCountry.france
      ),
      now,
      nowPlusOneYear,
      { sms: 0, minutes: 0, contacts: 0, leedbucks: 0 },
      {
        email_notification_copy: [],
        demo_account: false,
        beta_account: false,
        payment_wall: false
      },
      userId ?? '',
      SubscriptionDataSource.other,
      ''
    );
  }

  get idPlan(): string {
    return this._idPlan;
  }

  get name(): string {
    return this._name;
  }

  get credits(): ICreditMap {
    return this._credits;
  }

  get sender(): string {
    return this._sender;
  }

  get timezone(): SubscriptionTimezone {
    return this._timezone;
  }

  get planStart(): Date {
    return this._planStart;
  }

  get planEnd(): Date {
    return this._planEnd;
  }

  get settings(): SubscriptionSettings {
    return this._settings;
  }

  get employeeInChargeUid(): string {
    return this._employeeInChargeUid;
  }

  get datasource(): string {
    return this._datasource;
  }

  get notes(): string {
    return this._notes;
  }

  copyWith(data: Partial<SubscriptionPayload>): SubscriptionPayload {
    return new SubscriptionPayload(
      data.idPlan ?? this._idPlan,
      data.name ?? this._name,
      data.sender ?? this._sender,
      data.timezone ?? this._timezone,
      data.planStart ?? this._planStart,
      data.planEnd ?? this._planEnd,
      data.credits ?? this._credits,
      data.settings ?? this._settings,
      data.employeeInChargeUid ?? this._employeeInChargeUid,
      data.datasource ?? this._datasource,
      data.notes ?? this._notes
    );
  }

  toAPIPayload(): Record<string, any> {
    return {
      id_plan: this._idPlan,
      name: this._name,
      plan_start: this._planStart.getTime(),
      plan_end: this._planEnd.getTime(),
      sender: this._sender,
      timezone: this._timezone.toMapPatch(),
      credits: this._credits,
      settings: this._settings,
      employee_incharge_uid: this._employeeInChargeUid,
      datasource: this._datasource,
      notes: this._notes
    };
  }
}

export class AIPayload {
  private _action: string;
  private _behaviour: string;
  private _website: string;
  private _constraints: string;
  private _domain: string;
  private _fullname: string;
  private _gender: AIGender;
  private _intro: string;
  private _location: string;
  private _profile: string;
  private _target: string;
  private _ton: AITon;
  private _reminderTiming: number[];

  constructor(
    action: string,
    behaviour: string,
    website: string,
    constraints: string,
    domain: string,
    fullname: string,
    gender: AIGender,
    intro: string,
    location: string,
    profile: string,
    target: string,
    ton: AITon,
    reminderTiming: number[]
  ) {
    this._action = action;
    this._behaviour = behaviour;
    this._website = website;
    this._constraints = constraints;
    this._domain = domain;
    this._fullname = fullname;
    this._gender = gender;
    this._intro = intro;
    this._location = location;
    this._profile = profile;
    this._target = target;
    this._ton = ton;
    this._reminderTiming = reminderTiming;
  }

  static newDefault(): AIPayload {
    return new AIPayload(
      '',
      '',
      '',
      '',
      '',
      '',
      AIGender.woman,
      '',
      '',
      '',
      '',
      AITon.neutral,
      [24, 48]
    );
  }

  get action(): string {
    return this._action;
  }

  get behaviour(): string {
    return this._behaviour;
  }

  get website(): string {
    return this._website;
  }

  get constraints(): string {
    return this._constraints;
  }

  get domain(): string {
    return this._domain;
  }

  get fullname(): string {
    return this._fullname;
  }

  get gender(): AIGender {
    return this._gender;
  }

  get intro(): string {
    return this._intro;
  }

  get location(): string {
    return this._location;
  }

  get profile(): string {
    return this._profile;
  }

  get target(): string {
    return this._target;
  }

  get ton(): AITon {
    return this._ton;
  }

  get reminderTiming(): number[] {
    return this._reminderTiming;
  }

  copyWith(data: Partial<AIPayload>): AIPayload {
    return new AIPayload(
      data.action ?? this._action,
      data.behaviour ?? this._behaviour,
      data.website ?? this._website,
      data.constraints ?? this._constraints,
      data.domain ?? this._domain,
      data.fullname ?? this._fullname,
      data.gender ?? this._gender,
      data.intro ?? this._intro,
      data.location ?? this._location,
      data.profile ?? this._profile,
      data.target ?? this._target,
      data.ton ?? this._ton,
      data.reminderTiming ?? this._reminderTiming
    );
  }

  toAPIPayload(): Record<string, any> {
    return {
      action: this._action,
      behaviour: this._behaviour,
      website: this._website,
      constraints: this._constraints,
      domain: this._domain,
      fullname: this._fullname,
      gender: this._gender,
      intro: this._intro,
      location: this._location,
      profile: this._profile,
      target: this._target,
      ton: this._ton,
      reminder_timing: this._reminderTiming,
    };
  }
}
export class CreateUserPayload {
  private user: UserInfosPayload;
  private linkedSubscriptionId: string | null;
  private linkedCompanyId: string | null;
  private subscription: SubscriptionPayload;
  private ai: AIPayload;

  constructor(
    user: UserInfosPayload,
    linkedSubscriptionId: string | null,
    linkedCompanyId: string | null,
    subscription: SubscriptionPayload,
    ai: AIPayload
  ) {
    this.user = user;
    this.linkedSubscriptionId = linkedSubscriptionId;
    this.linkedCompanyId = linkedCompanyId;
    this.subscription = subscription;
    this.ai = ai;
  }

  toAPIPayload(): Record<string, any> {
    const baseUserPayload = this.user.toAPIPayload();
    const isLinkedToCompany = !!this.linkedCompanyId && this.linkedCompanyId.trim() !== '';

    return {
      // When creating a user from a company page, we must use the leedflow "simple user"
      // account type so backend links the user to `id_companies` (and not subscriptions).
      user: {
        ...baseUserPayload,
        type: isLinkedToCompany ? AccountType.USER : baseUserPayload.type,
      },
      linked_subscription_id: this.linkedSubscriptionId,
      linked_company_id: this.linkedCompanyId || '',
      origin: isLinkedToCompany ? EProductType.LEEDFLOW : undefined,
      subscription: this.subscription.toAPIPayload(),
      ai: this.ai.toAPIPayload(),
    };
  }
}

export class CreateUserFailure extends Failure {
  constructor(message = 'Unknown error', code = 'unknown-error') {
    super(message, code);
  }
}

export default createUser;
